/**
 * Slice-style unit issue report during Clean (and anywhere in turnover).
 * Room · note · Photo or Video proof → maintenance_issue_reports.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Camera, ChevronDown, Loader2, Video, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useChecklistById } from "@/hooks/useChecklists";
import { useUnitItems } from "@/hooks/useUnitItems";
import { itemLabel, sameRoom } from "@/lib/photoGroups";
import { useCreateMaintenanceReport } from "@/hooks/useMaintenanceReports";
import { sendMaintenanceIssuesEmail } from "@/lib/notificationEmails";
import {
  captureFrameFromVideo,
  uploadInAppCleaningPhoto,
} from "@/lib/cleaningPhotoCapture";
import { pickVideoRecorderMime } from "@/lib/mediaUrl";
import { MediaThumb } from "@/components/media/MediaThumb";
import type { Room } from "@/types/checklist";

type CaptureKind = "photo" | "video";

const MAX_VIDEO_SECONDS = 45;

interface TurnoverUnitIssueReportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  propertyId: string;
  cleaningTaskId: string;
  cleanerId: string;
  checklistId?: string | null;
  checklistName?: string | null;
  tDynamic: (s: string) => string;
}

function formatRecordTime(secs: number): string {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

export function TurnoverUnitIssueReportDialog({
  open,
  onOpenChange,
  propertyId,
  cleaningTaskId,
  cleanerId,
  checklistId,
  checklistName,
  tDynamic,
}: TurnoverUnitIssueReportDialogProps) {
  const createReport = useCreateMaintenanceReport();
  const { data: checklist } = useChecklistById(checklistId ?? undefined);
  const { data: unitItems = [] } = useUnitItems(propertyId);

  const roomOptions = useMemo(() => {
    const names = new Set<string>();
    try {
      const rooms = (checklist?.rooms as Room[]) || [];
      for (const r of rooms) {
        const n = r.name?.trim();
        if (n) names.add(n);
      }
    } catch {
      /* ignore malformed rooms */
    }
    names.add(tDynamic("General"));
    return [...names];
  }, [checklist?.rooms, tDynamic]);

  const [room, setRoom] = useState("");
  const [itemId, setItemId] = useState<string | null>(null);
  const [issue, setIssue] = useState("");
  const [captureKind, setCaptureKind] = useState<CaptureKind>("photo");
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [mediaPreview, setMediaPreview] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const recordChunksRef = useRef<Blob[]>([]);
  const recordTimerRef = useRef<number | null>(null);
  const recordMimeRef = useRef(pickVideoRecorderMime());
  const shutterLock = useRef(false);

  const stopCamera = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      try {
        mediaRecorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }
    mediaRecorderRef.current = null;
    if (recordTimerRef.current != null) {
      window.clearInterval(recordTimerRef.current);
      recordTimerRef.current = null;
    }
    setRecording(false);
    setRecordSecs(0);
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    setCameraReady(false);
  }, []);

  const resetForm = useCallback(() => {
    stopCamera();
    setRoom("");
    setItemId(null);
    setIssue("");
    setCaptureKind("photo");
    setMediaUrl(null);
    setMediaPreview(null);
    setCapturing(false);
    setUploading(false);
    shutterLock.current = false;
  }, [stopCamera]);

  const handleOpenChange = (next: boolean) => {
    if (!next) resetForm();
    onOpenChange(next);
  };

  useEffect(() => {
    if (!open || !capturing) {
      stopCamera();
      return;
    }
    let cancelled = false;
    const start = async () => {
      setCameraReady(false);
      if (!navigator.mediaDevices?.getUserMedia) return;
      try {
        stopCamera();
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: captureKind === "video",
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          video.setAttribute("playsinline", "true");
          video.muted = true;
          await video.play().catch(() => undefined);
        }
        setCameraReady(true);
      } catch (err) {
        console.error("[TurnoverUnitIssueReportDialog] camera", err);
        setCameraReady(false);
      }
    };
    void start();
    return () => {
      cancelled = true;
      stopCamera();
    };
  }, [open, capturing, captureKind, stopCamera]);

  const uploadBlob = async (blob: Blob, ext: string, contentType: string) => {
    setUploading(true);
    try {
      const path = `${propertyId}/${cleaningTaskId}/unit-issues/${crypto.randomUUID()}.${ext}`;
      const { error } = await supabase.storage.from("cleaning-photos").upload(path, blob, {
        contentType,
        upsert: false,
      });
      if (error) throw error;
      const { data } = supabase.storage.from("cleaning-photos").getPublicUrl(path);
      setMediaUrl(data.publicUrl);
      setMediaPreview(data.publicUrl);
      setCapturing(false);
      stopCamera();
    } finally {
      setUploading(false);
    }
  };

  const uploadVideoBlob = async (blob: Blob, ext: string) => {
    const contentType = blob.type || (ext === "mp4" ? "video/mp4" : "video/webm");
    await uploadBlob(blob, ext, contentType);
  };

  const captureStill = async () => {
    if (shutterLock.current || uploading) return;
    const video = videoRef.current;
    if (!video || !cameraReady) {
      toast({
        title: tDynamic("Camera required"),
        description: tDynamic("Allow camera access and take the photo in the app."),
        variant: "destructive",
      });
      return;
    }
    shutterLock.current = true;
    try {
      const frame = await captureFrameFromVideo(video);
      const { url } = await uploadInAppCleaningPhoto({
        propertyId,
        taskId: cleaningTaskId,
        moment: "issue",
        blob: frame.blob,
        width: frame.width,
        height: frame.height,
        storagePath: `${propertyId}/${cleaningTaskId}/unit-issues/${crypto.randomUUID()}.jpg`,
      });
      setMediaUrl(url);
      setMediaPreview(url);
      setCapturing(false);
      stopCamera();
    } catch (err) {
      toast({
        title: tDynamic("Capture failed"),
        description: err instanceof Error ? err.message : tDynamic("Try again"),
        variant: "destructive",
      });
    } finally {
      shutterLock.current = false;
    }
  };

  const stopRecording = useCallback(() => {
    const rec = mediaRecorderRef.current;
    if (!rec || rec.state === "inactive") return;
    try {
      rec.stop();
    } catch {
      /* ignore */
    }
  }, []);

  const startRecording = async () => {
    if (shutterLock.current || uploading || recording) return;
    const stream = streamRef.current;
    if (!stream || !cameraReady) {
      toast({
        title: tDynamic("Camera required"),
        description: tDynamic("Allow camera access to record in the app."),
        variant: "destructive",
      });
      return;
    }
    if (typeof MediaRecorder === "undefined") {
      toast({
        title: tDynamic("Video not supported"),
        description: tDynamic("Use Photo on this device"),
        variant: "destructive",
      });
      return;
    }
    shutterLock.current = true;
    try {
      recordMimeRef.current = pickVideoRecorderMime();
      recordChunksRef.current = [];
      let rec: MediaRecorder;
      try {
        rec = new MediaRecorder(stream, { mimeType: recordMimeRef.current.mimeType });
      } catch {
        rec = new MediaRecorder(stream);
        const fallbackType = rec.mimeType || "video/webm";
        recordMimeRef.current = {
          mimeType: fallbackType,
          ext: fallbackType.includes("mp4") ? "mp4" : "webm",
        };
      }
      mediaRecorderRef.current = rec;
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) recordChunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        if (recordTimerRef.current != null) {
          window.clearInterval(recordTimerRef.current);
          recordTimerRef.current = null;
        }
        setRecording(false);
        setRecordSecs(0);
        const chunks = recordChunksRef.current;
        recordChunksRef.current = [];
        mediaRecorderRef.current = null;
        shutterLock.current = false;
        if (!chunks.length) return;
        const blob = new Blob(chunks, { type: recordMimeRef.current.mimeType });
        void uploadVideoBlob(blob, recordMimeRef.current.ext).catch((err) => {
          toast({
            title: tDynamic("Video upload failed"),
            description: err instanceof Error ? err.message : tDynamic("Try again"),
            variant: "destructive",
          });
        });
      };
      rec.start(250);
      setRecording(true);
      setRecordSecs(0);
      recordTimerRef.current = window.setInterval(() => {
        setRecordSecs((s) => {
          const next = s + 1;
          if (next >= MAX_VIDEO_SECONDS) stopRecording();
          return next;
        });
      }, 1000);
    } catch (err) {
      shutterLock.current = false;
      toast({
        title: tDynamic("Couldn’t record"),
        description: err instanceof Error ? err.message : tDynamic("Try Photo instead"),
        variant: "destructive",
      });
    }
  };

  const onShutter = () => {
    if (captureKind === "video") {
      if (recording) stopRecording();
      else void startRecording();
      return;
    }
    void captureStill();
  };

  const handleSubmit = async () => {
    const roomName = room.trim() || tDynamic("General");
    const description = issue.trim();
    if (!description) {
      toast({
        title: tDynamic("Describe the issue"),
        description: tDynamic("Add details so the owner knows what to fix."),
        variant: "destructive",
      });
      return;
    }

    setSubmitting(true);
    try {
      const photoUrls = mediaUrl ? [mediaUrl] : [];
      const summary = description.length > 120 ? `${description.slice(0, 117)}…` : description;

      await createReport.mutateAsync({
        property_id: propertyId,
        cleaning_task_id: cleaningTaskId,
        task_description: summary,
        room_name: roomName,
        checklist_name: checklistName ?? undefined,
        cleaner_id: cleanerId,
        response: "needs_repair",
        notes: description,
        photos: photoUrls,
        unit_item_id: itemId,
      });

      await sendMaintenanceIssuesEmail({
        propertyId,
        taskId: cleaningTaskId,
        issues: [
          {
            room_name: roomName,
            description,
            photo_url: photoUrls[0] ?? null,
          },
        ],
      });

      toast({
        title: tDynamic("Issue reported"),
        description: tDynamic("The owner was notified."),
      });
      handleOpenChange(false);
    } catch (err) {
      console.error("[TurnoverUnitIssueReportDialog]", err);
      toast({
        title: tDynamic("Error"),
        description: err instanceof Error ? err.message : tDynamic("Failed to send report"),
        variant: "destructive",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  const busy = submitting || uploading;

  return (
    <div className="fixed inset-0 z-[80] flex flex-col justify-end">
      <button
        type="button"
        className="absolute inset-0 bg-[rgba(15,23,32,0.45)]"
        aria-label={tDynamic("Close")}
        disabled={busy || recording}
        onClick={() => !busy && !recording && handleOpenChange(false)}
      />

      <div className="relative z-10 flex max-h-[92dvh] flex-col overflow-hidden rounded-t-[26px] bg-white shadow-[0_-12px_34px_rgba(15,23,32,0.18)]">
        <div className="flex shrink-0 items-start justify-between gap-3 px-5 pb-1 pt-[22px]">
          <div className="flex min-w-0 items-start gap-2.5">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-[#b45309]" strokeWidth={2.4} />
            <div className="min-w-0">
              <div className="text-[22px] font-bold tracking-[-0.3px] text-[#0f1720]">
                {tDynamic("Report unit issue")}
              </div>
              <div className="mt-1 text-[14px] font-medium leading-snug text-[#8494a2]">
                {tDynamic("Report damage or problems from anywhere during this turnover.")}
              </div>
            </div>
          </div>
          <button
            type="button"
            disabled={busy || recording}
            onClick={() => handleOpenChange(false)}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[#94a3b8] hover:bg-[#f4f7f9]"
            aria-label={tDynamic("Close")}
          >
            <X className="h-5 w-5" strokeWidth={2.4} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] pt-4">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-[14px] font-bold text-[#0f1720]">{tDynamic("Room")}</label>
              <div className="relative">
                <select
                  value={room}
                  onChange={(e) => {
                    setRoom(e.target.value);
                    setItemId(null);
                  }}
                  disabled={busy}
                  className={cn(
                    "h-[54px] w-full appearance-none rounded-[14px] border-[1.5px] bg-white pl-4 pr-11 text-[16px] font-medium text-[#0f1720] outline-none",
                    room ? "border-[#24a89a]" : "border-[#e6edf1]",
                  )}
                >
                  <option value="">{tDynamic("Select a room")}</option>
                  {roomOptions.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-5 w-5 -translate-y-1/2 text-[#8494a2]" />
              </div>
            </div>

            {room && unitItems.filter((i) => sameRoom(i.room, room)).length > 0 ? (
              <div className="flex flex-col gap-1.5">
                <label className="text-[14px] font-bold text-[#0f1720]">{tDynamic("Which item?")}</label>
                <div className="flex flex-wrap gap-2">
                  {unitItems
                    .filter((i) => sameRoom(i.room, room))
                    .map((i) => (
                      <button
                        key={i.id}
                        type="button"
                        disabled={busy}
                        onClick={() => setItemId(itemId === i.id ? null : i.id)}
                        className={cn(
                          "min-h-[48px] rounded-[13px] border-[1.5px] px-4 text-[15.5px] font-bold",
                          itemId === i.id ? "border-[#24a89a] bg-[#e2eeed] text-[#15776d]" : "border-[#e6edf1] bg-white text-[#0f1720]",
                        )}
                      >
                        {itemLabel(i.name, i.quantity)}
                      </button>
                    ))}
                </div>
              </div>
            ) : null}

            <div className="flex flex-col gap-1.5">
              <label className="text-[14px] font-bold text-[#0f1720]">{tDynamic("Issue")}</label>
              <textarea
                value={issue}
                onChange={(e) => setIssue(e.target.value)}
                rows={4}
                placeholder={tDynamic("What's wrong? Include location and details.")}
                disabled={busy}
                className="min-h-[110px] w-full resize-none rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white px-4 py-3.5 text-[16px] font-medium text-[#0f1720] placeholder:text-[#94a3b8] outline-none focus:border-[#24a89a]"
              />
            </div>

            <div className="flex flex-col gap-2.5">
              <label className="text-[14px] font-bold text-[#0f1720]">
                {tDynamic("Photo or video proof")}
              </label>

              <div className="flex gap-1.5 rounded-[13px] bg-[#f4f7f9] p-1">
                {(
                  [
                    { id: "photo" as const, label: tDynamic("Photo"), Icon: Camera },
                    { id: "video" as const, label: tDynamic("Video"), Icon: Video },
                  ] as const
                ).map((opt) => {
                  const active = captureKind === opt.id;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      disabled={busy || recording || capturing}
                      onClick={() => setCaptureKind(opt.id)}
                      className={cn(
                        "flex flex-1 items-center justify-center gap-2 rounded-[10px] py-[9px] text-[15px] font-bold",
                        active
                          ? "bg-white text-[#0f1720] shadow-[0_1px_3px_rgba(15,23,32,0.08)]"
                          : "text-[#64748b]",
                      )}
                    >
                      <opt.Icon className="h-4 w-4" strokeWidth={2.4} />
                      {opt.label}
                    </button>
                  );
                })}
              </div>

              {capturing ? (
                <div className="relative overflow-hidden rounded-[18px] bg-[#1c2732]">
                  <div className="relative aspect-[4/3] w-full">
                    <video
                      ref={videoRef}
                      playsInline
                      muted
                      autoPlay
                      className={cn(
                        "absolute inset-0 h-full w-full object-cover",
                        !cameraReady && "opacity-0",
                      )}
                    />
                    {!cameraReady ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-4 text-center">
                        <Loader2 className="h-7 w-7 animate-spin text-white/70" />
                        <p className="text-[13px] font-medium text-white/60">
                          {tDynamic("Starting camera…")}
                        </p>
                        <p className="text-[11px] font-medium text-white/40">
                          {tDynamic("Photos must be taken in the app")}
                        </p>
                      </div>
                    ) : null}
                    {recording ? (
                      <div className="absolute left-3 top-3 flex items-center gap-2 rounded-full bg-[#b4453a] px-2.5 py-1 text-[12px] font-bold text-white">
                        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-white" />
                        {formatRecordTime(recordSecs)}
                      </div>
                    ) : null}
                  </div>
                  <div className="flex items-center justify-between gap-3 px-4 py-3.5">
                    <button
                      type="button"
                      disabled={busy || recording}
                      onClick={() => {
                        stopCamera();
                        setCapturing(false);
                      }}
                      className="text-[14px] font-bold text-white/70"
                    >
                      {tDynamic("Cancel")}
                    </button>
                    <button
                      type="button"
                      disabled={uploading}
                      onClick={onShutter}
                      aria-label={
                        captureKind === "video"
                          ? recording
                            ? tDynamic("Stop recording")
                            : tDynamic("Start recording")
                          : tDynamic("Capture")
                      }
                      className={cn(
                        "flex h-[68px] w-[68px] items-center justify-center rounded-full border-[4px] active:scale-95",
                        recording ? "border-[#b4453a]" : "border-white/55",
                      )}
                    >
                      <span
                        className={cn(
                          recording
                            ? "h-7 w-7 rounded-[7px] bg-[#b4453a]"
                            : captureKind === "video"
                              ? "h-[52px] w-[52px] rounded-full bg-[#b4453a]"
                              : "h-[52px] w-[52px] rounded-full bg-white",
                        )}
                      />
                    </button>
                    <div className="w-12" aria-hidden />
                  </div>
                </div>
              ) : mediaPreview ? (
                <div className="relative overflow-hidden rounded-[18px] border-[1.5px] border-[#e6edf1]">
                  <div className="aspect-[4/3] w-full bg-[#eff4f7]">
                    <MediaThumb url={mediaPreview} />
                  </div>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setMediaUrl(null);
                      setMediaPreview(null);
                    }}
                    className="absolute right-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-full bg-[rgba(15,23,32,0.65)] text-white"
                    aria-label={tDynamic("Remove")}
                  >
                    <X className="h-4 w-4" strokeWidth={2.6} />
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setCapturing(true)}
                    className="absolute bottom-2.5 left-2.5 rounded-full bg-white/95 px-3 py-1.5 text-[12px] font-bold text-[#0f1720]"
                  >
                    {captureKind === "video" ? tDynamic("Retake video") : tDynamic("Retake photo")}
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setCapturing(true)}
                  className="flex h-[56px] w-full items-center justify-center gap-2.5 rounded-[14px] border-[1.5px] border-dashed border-[#c5d2db] bg-[#f8fafb] text-[15px] font-bold text-[#64748b] active:scale-[0.99]"
                >
                  {captureKind === "video" ? (
                    <Video className="h-5 w-5" strokeWidth={2.3} />
                  ) : (
                    <Camera className="h-5 w-5" strokeWidth={2.3} />
                  )}
                  {captureKind === "video"
                    ? tDynamic("Record video (optional)")
                    : tDynamic("Add photo (optional)")}
                </button>
              )}
            </div>

            <button
              type="button"
              disabled={busy || recording}
              onClick={() => void handleSubmit()}
              className="mt-1 flex h-[56px] w-full items-center justify-center rounded-[16px] bg-[#24a89a] text-[18px] font-bold text-white hover:bg-[#1f958a] active:bg-[#1b8378] disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : tDynamic("Send report")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
